import {BackendStore} from "../stores/BackendStore";
import {ImageStore} from "../stores/ImageStore";
import {EntropyStore} from "../stores/EntropyStore";
import {AuthenticationStore} from "../stores/AuthenticationStore";
import {Api} from "../api";

import Web3 from "web3";

import {Entropy, ImageRef} from "../types";

import {snooze} from "../utils";

import {PriceEntry, PriceStore} from "../stores/PriceStore";
import {AuctionInfo, BidStore} from "../stores/BidStore";
import {RoundStore} from "../stores/RoundStore";
import {CountDownStore} from "../stores/CountDownStore";
import {Web3Store} from "../stores/Web3Store";
import {DutchAuction} from "../dutch-auction/contract";
import {autorun} from "mobx";
import {UserVoteSet, UserVoteStore} from '../stores/UserVoteStore';
import BN from "bn.js";
import {FET_ERC20} from '../dutch-auction/constants';
import {EXPECTED_BLOCK_TIME, NETWORK, RESERVE_PRICE_DURATION} from '../constants'
import jwt_decode, {JwtPayload} from 'jwt-decode';
import {WinnerStore} from "../stores/WinnerStore";

declare let window: any;

export class StateService {
  backend = new BackendStore();
  images = new ImageStore();
  authentication = new AuthenticationStore();
  api = new Api(this.backend.get());
  priceStore = new PriceStore();
  bidStore = new BidStore();
  winners = new WinnerStore();
  entropy = new EntropyStore();
  rounds = new RoundStore();
  countDown = new CountDownStore();
  web3 = new Web3Store();
  userVotes = new UserVoteStore();

  constructor() {
    this.onClickLoginMeta = this.onClickLoginMeta.bind(this);
    this.onClickLogout = this.onClickLogout.bind(this);

    const disposer = autorun(() => {
      if (this.web3.isAvailable()) {
        try {
          this.contractSync().catch(error => {
            console.log("Failed to contract sync after web3 available: ", error);
          });
          disposer();
        } catch (error) {
          console.log("Failed to contract sync: ", error);
        }
      }
    });
  }

  async backgroundSync(): Promise<void> {
    // noinspection InfiniteLoopJS
    while (true) {
      await this.performSync();
      await snooze(15000);
    }
  }

  async sync1(): Promise<boolean>{
    await this.syncRounds();
    await this.syncUserVotes();
    return true;
  }

  async performSync(): Promise<boolean> {
    try {
      await Promise.all([
        this.syncAuth(),
        this.syncWinners(),
        this.syncEntropy(),
        this.contractSync(),
        this.sync1(),
        this.syncImages(),
        this.syncUserImages(),
        this.syncCountDown(),
      ]);
    } catch (e) {
      return false;
    }

    return true;
  }

  async syncAuth(): Promise<void> {
    if (this.authentication.getAuthStatus()){
      await this.checkToken();
    }
  }

  async syncImages(): Promise<void> {
    try {
      const images = await this.collectImages();
      if (images === null) {
        return;
      }

      // Do NOT update the image store if the user is in the process of
      // selecting their images, to avoid them jumping around
      if (this.images.allImageSelected(this.rounds.getActiveRound()) || this.images.getSelection(this.rounds.getActiveRound()).length === 0) {
        this.images.addAllImages(images);
      }
    } catch (error) {
      console.log("Failed to sync images: ", error);
    }
  }

  async syncUserVotes(): Promise<void> {
    try {
      let votingRounds = this.rounds.getVotingRounds();

      // query all the votes for the rounds
      let userVoteSets: UserVoteSet[] = [];
      for (let round of votingRounds) {
        const votes = await this.api.listVotes(round.name);
        userVoteSets.push({
          roundName: round.name,
          votes,
        })
      }

      this.userVotes.addUserVotes(userVoteSets);
    } catch (error) {
      console.log("Failed to sync user votes: ", error);
    }
  }

  // Get the images our user has voted for and update the images db if not already
  async syncUserImages(): Promise<void> {
    try {
      let user = this.userVotes.getThisUser();

      if (user !== "") {
        const userImages = await this.api.listUserImages(user);

        this.images.updateUserImages(userImages);
      }
    } catch (error) {
      console.log("Failed to sync user images: ", error);
    }
  }

  async syncRounds(): Promise<void> {
    try {
      let rounds = await this.api.listRounds();
      this.rounds.update(rounds);
      for (let round of rounds) {
        if (round.vote) {
          this.images.setMaxSelection(round.name, round.n_votes ?? 5);
        }
      }
    } catch (error) {
      console.log("Failed to sync rounds: ", error);
    }
  }

  async syncCountDown(): Promise<void> {
    this.countDown.update(await this.api.countDown());
  }

  async syncEntropy(): Promise<void> {
    const entropys = await this.collectEntropy();

    if (entropys === null) {
      return;
    }

    for (const entropy of entropys) {
      this.entropy.update(entropy);
    }
  }

  async syncWinners(): Promise<void> {
    const users = await this.api.listWinners();

    if (users === null) {
      return;
    }

    for (const user of users) {
      this.winners.add(user);
    }
  }

  // Functions to collect the data from the API
  async collectImages(): Promise<ImageRef[] | null> {
    return this.api.listImages();
  }

  async collectEntropy(): Promise<Entropy[] | null> {
    return this.api.listEntropy();
  }

  async getToken(address: string, signature: string): Promise<boolean> {
    let resp = await this.api.getToken(address, signature);
    console.log("Authenticated: ", resp);
    if (resp.statusCode === 200) {
      localStorage.setItem("user_address", address);
      this.authentication.setAddress(address);
      this.authentication.setAuthenticationStatus(true);
      this.userVotes.setThisUser(address);
      return true;
    } else {
      this.authentication.setAuthenticationStatus(false);
      return false;
    }
  }

  async checkToken() {
    // has it expired? If so update it
    const access_token = localStorage.getItem("access_token") || '';
    try {
      let tokenInfo: JwtPayload = jwt_decode<JwtPayload>(access_token);
      let expireInterval = (tokenInfo.exp || 0) - Date.now() / 1000;
      if (expireInterval < 5 * 60) {
        let resp = await this.api.refreshToken();
        if (resp.statusCode === 200) {
          console.log("Refreshed login token")
          this.authentication.setAuthenticationStatus(true);
        } else {
          console.log("Failed to refresh login token: ", resp.title, resp.detail)
          this.authentication.setAuthenticationStatus(false);
        }
      }
    }
    catch(Error){
      console.log("Failed to refresh login token")
      this.authentication.setAuthenticationStatus(false);
    }
  }

  async addPricePoints(
    contract: DutchAuction,
    info: AuctionInfo
  ) {
    let start = info.start;
    if (start * 1 === 0){  // even though start is a number it requires the *1 to make the === comparison work
      this.priceStore.clearPrice();
      return;
    }
    let end = info.end;
    let startStake = (new BN(info.startStake)).div(FET_ERC20.multiplier).toNumber();
    let reservedStake = (new BN(info.reserveStake)).div(FET_ERC20.multiplier).toNumber();
    let POINTS_TO_CONSTRUCT = 9;
    let startTimeRet = await contract.getBlockTime(start);
    const points: PriceEntry[] = [];

    // startTime will be null if it is in the future, so estimate it
    if (!startTimeRet) {
      let currentBlock = await contract.getBlockNumber();
      let currentTimeRet = await contract.getBlockTime(currentBlock);
      let currentTime = (typeof currentTimeRet === "string" ? Number.parseInt(currentTimeRet) : currentTimeRet);
      startTimeRet = currentTime + (start - currentBlock) * EXPECTED_BLOCK_TIME;
    }

    // Calculate what the end time would be, assuming fixed block time(!)
    let startTime = (typeof startTimeRet === "string" ? Number.parseInt(startTimeRet) : startTimeRet);
    let endTime = startTime + ((end - RESERVE_PRICE_DURATION - start) *EXPECTED_BLOCK_TIME);
    let duration = endTime - startTime;
    let priceDropPerIncrement = (startStake - reservedStake)/POINTS_TO_CONSTRUCT;
    let timeDropPerIncrement = duration/POINTS_TO_CONSTRUCT;

    for (let i = 0;i < POINTS_TO_CONSTRUCT;i++) {
      points.push({
        blockNumber: i,
        price: Math.round(startStake - (i * priceDropPerIncrement)),
        time: Math.round(startTime + (i * timeDropPerIncrement)),
      })
    }

    // avoid rounding
    points.push({
      blockNumber: POINTS_TO_CONSTRUCT,
      price: Math.round(reservedStake),
      time: Math.round(endTime),
    })

    this.priceStore.addPricePoints(points);
  }

  async contractSync() {
    if (!this.web3.isAvailable()) {
      return;
    }
    const contract = this.web3.getContract() as NonNullable<DutchAuction>;
    try {
      let info = await contract.getInfo();

      let initial = false;
      if (this.bidStore.getAuctionInfo().start === 0 || info.start !== this.bidStore.getAuctionInfo().start) {
        initial = true;
      }

      // update auction info
      this.bidStore.setAuctionInfo(info);

      // update price graph - needs to run every time to trigger re-render for purple hue of line
      await this.addPricePoints(
          contract,
          info
      );

      let finalized = await contract.isFinalized();
      let currentPrice = await contract.getCurrentPrice();

      if (currentPrice){
        this.priceStore.setCurrentPrice(currentPrice);
      }

      this.bidStore.updateFinalized(finalized);
      this.bidStore.updateCleared(info == null || info.end === "0");

      if (!finalized) {
        let stakers = await contract.getStakers();
        stakers = stakers.filter(function(e: string) { return e !== '0x0000000000000000000000000000000000000000' });

        if (initial) {
          this.bidStore.clear();
        }

        let slotsAvail = info.slotsOnSale;

        // Get a list of stakers (filtered to remove null values), and determine the slots that each of them would get
        // if the auction were to end now. Their slots will generally increase over time as the slot price decreases
        if (stakers) {
          let deposits = [];

          for (let i = 0;i < stakers.length;i++) {
            let deposit = await contract.getDeposits(stakers[i]);
            deposits.push(deposit);
          }

          let slotAllocations = PredictAllocatedSlots(deposits, currentPrice, info.slotsOnSale);

          for (let i = 0; i < slotAllocations.length; i++) {
            slotsAvail -= slotAllocations[i];
            this.bidStore.update(
              stakers[i].toLowerCase(),
              slotAllocations[i],
              false,
              deposits[i]
            );
          }
        }

        this.bidStore.updateSlots(
          slotsAvail,
          info.slotsOnSale
        );
      } else {
        let stakers = await contract.getFinalStakers();
        if (initial) {
          this.bidStore.clear();
        }

        // The slot info from the contract is only accurate after finalized.
        this.bidStore.updateSlots(
          info.slotsOnSale - info.slotsSold,
          info.slotsOnSale
        );

        for (let key in stakers) {
          if (key.search("0x0000") !== -1) {
            break;
          }

          let deposit = await contract.getDeposits(key) ?? 0;
          let stake = await contract.calculateSelfStake(key);
          this.bidStore.update(key.toLowerCase(), stakers[key], true, deposit, stake);
        }
      }
      let biddingPhase = await contract.isBiddingPhase();
      this.bidStore.updateBiddingPhase(biddingPhase);
      let staker = this.authentication.getAddress();
      if (staker !== "NONE" && finalized) {
        await this.updateSelfDepositAndStake(staker);
      }

      this.web3.setSyncContract(true);
    } catch (error) {
      console.log("Error in contract sync: ", error);
    }
  }

  async updateSelfDepositAndStake(staker: string) {
    if (!this.web3.isAvailable()) {
      return;
    }
    const contract = this.web3.getContract() as NonNullable<DutchAuction>;
    let selfDeposit = await contract.getDeposits(staker);
    let selfStakeNeeded = await contract.calculateSelfStake(staker);
    this.bidStore.updateSelfDepositAndStake(selfDeposit, selfStakeNeeded);
  }

  // Log the user out if they change their address/network
  attachAddressChangeHandlers() {
    window.ethereum.on('accountsChanged', (accounts:any) => {
      console.log('accountsChanges',accounts);
      this.onClickLogout(null);
    });

    // detect Network account change
    window.ethereum.on('networkChanged', (networkId: any) => {
      console.log('networkChanged',networkId);
      this.onClickLogout(null);
    });
  }

  async performInitialSync() {
    let randHex = (len: number) => {
      let maxlen = 8,
        min = Math.pow(16, Math.min(len, maxlen) - 1),
        max = Math.pow(16, Math.min(len, maxlen)) - 1,
        n = Math.floor(Math.random() * (max - min + 1)) + min,
        r = n.toString(16);
      while (r.length < len) {
        r = r + randHex(len - maxlen);
      }
      return r;
    };
    await this.syncEntropy();

    // Add dummy points when it is behind opaque screen
    const points: PriceEntry[] = [];
    for (let i = 0; i < 11; i++) {
      points.push({
        blockNumber: 1,
        price:  2000 - (i * 10),
        time: i * 100
      })
    }
    this.priceStore.addPricePoints(points)

    const entries = this.entropy.getEntropy();
    if (entries.length > 0) {
      entries.forEach((v) => {
        this.bidStore.update(v.address, 0, false, 0);
      });
    } else {
      for (let i = 0; i < 10; i++) {
        this.bidStore.update("0x" + randHex(42), 0, false, 0);
      }
    }

    this.bidStore.updateSlots(0, 0);

    let address = localStorage.getItem("user_address");
    if (address) {
      this.authentication.setAddress(address);
      this.authentication.setAuthenticationStatus(true);
      this.userVotes.setThisUser(address);
      if (window.ethereum) {
        let web3 = new Web3(window.ethereum);
        try {
          const accounts = await window.ethereum.request({
            method: "eth_requestAccounts",
          });
          const metaAddress = accounts[0].toLowerCase();
          this.web3.setWeb3(web3);

          if (metaAddress !== address) {
            console.log("Address from local storage different from metamask address!");
            this.onClickLogout(null);
          }

          this.attachAddressChangeHandlers()
        } catch (e) {
          console.log("FAILED to connect to metamask: ", e);
        }
      }
    }
  }

  async connectMeta() {
    if (window.ethereum) {
      let web3 = new Web3(window.ethereum);
      try {
        this.web3.setWeb3(web3);

        this.attachAddressChangeHandlers()
        return [true, ""];
      } catch (e) {
        console.log("FAILED to connect metamask: ", e);
        return [false, "Failed to connect metamask!"];
      }
    }
    return [false, "You have to install MetaMask in order to use this site!"];
  }

  async onClickLoginMeta(event: any) {
    event.preventDefault();
    const [ status, error ] = await this.connectMeta();
    if (!status) {
      return [status, error];
    }

    this.contractSync();

    try {
      const web3 = this.web3.getWeb3();

      // Check you are on the right network before authenticating
      const netId = await web3?.eth.net.getId()

      if(netId) {
        switch (netId) {
          case 1:
            if (NETWORK !== "mainnet") {
              return [false, `Metamask is configured for 'mainnet' but you should be on '${NETWORK}'`]
            }
            break
          case 3:
            if (NETWORK !== "ropsten") {
              return [false, `Metamask is configured for 'ropsten' but you should be on '${NETWORK}'`]
            }
            break
          case 4:
            if (NETWORK !== "rinkeby") {
              return [false, `Metamask is configured for 'rinkeby' but you should be on '${NETWORK}'`]
            }
            break
          default:
            return [false, `Metamask is configured for 'unknown' chain but you should be on '${NETWORK}'`]
        }
      }

      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts",
      });
      const address = accounts[0].toLowerCase();
      this.authentication.setAddress(address);

      let nonce = await this.api.getNonce(address);
      if (nonce === null)
      {
        console.log("FAILED to login: no nonce returned, user probably does not have slots.");
        return [false, "Failed to login: user not registered. But your MetaMask is now connected."];
      }
      let signature = await web3?.eth.personal.sign(nonce, address, "");
      let tokenStatus = await this.getToken(address, signature ?? "");

      if (!tokenStatus){
        console.log("FAILED to login: signature incorrect");
        return [false, "Failed to login: signature incorrect"];
      }

      await this.performSync();
      return [true, "Successfully logged in"];
    } catch (e) {
      console.log("FAILED to auth with metamask: ", e);
      return [false, "Failed to login!"];
    }
  }

  onClickLogout(event: any) {
    event?.preventDefault();
    localStorage.removeItem("user_address");
    localStorage.removeItem("access_token");
    this.authentication.setAddress("NONE");
    this.authentication.setAuthenticationStatus(false);
    this.userVotes.setThisUser("");    
    this.web3.setSyncContract(false);
  }

}

// Determine, if the auction were to end now at the current price, what slots everyone would get.
// Requires that the stakers are ordered as they bid in the auction!
function PredictAllocatedSlots(deposits: number[], currentPrice: number, slotsOnSale: number) : number[] {
  let slots = slotsOnSale;
  let ret = [];

  for (let i = 0; i < deposits.length; i++) {
    if (slots <= 0) {
      ret.push(0);
    }

    let getSlots = Math.min(Math.floor(+deposits[i]/currentPrice), slots);

    slots -= getSlots;
    ret.push(getSlots);
  }

  return ret;
}

